import type { Webviewer } from "lib";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useCameraState } from "contexts/cameraState";
import { useDispatchCoachmarks } from "contexts/coachmarks";
import { drawersActions, useDispatchDrawers, useDrawers } from "contexts/drawers";
import { explorerGlobalsActions, useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useLazyHidden } from "contexts/hidden";
import { useDispatchHighlighted, useHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { usePanelMode } from "contexts/panelMode";
import { isMobileUxFlavor } from "contexts/panels";
import { useDispatchProcoreStampMenuItems } from "contexts/procoreStamp";
import { useDispatchPropertiesTabs } from "contexts/propertiesTabs";
import { useDispatchSelectionBasket, useLazySelectionBasket } from "contexts/selectionBasket";
import { sidePanelsActions, useDispatchSidePanels, useSidePanels } from "contexts/sidePanels";
import { useSvgRenderer } from "contexts/svgRenderer";
import { useDispatchToolbars, useToolbarButtonOrder } from "contexts/toolbars";
import { useDispatchWindows } from "contexts/windows";
import { useResetView } from "features/home/useResetView";
import {
    selectObject2NormalColorOverrides,
    selectObject2SelectedColorOverrides,
    selectObject2XrayColorOverrides,
    selectSceneStatus,
} from "features/render";
import { convertUnitSystemToDisplayUnit } from "features/uom/utils";
import { useGetViewpoint } from "features/viewpoints/useGetViewpoint";
import { useSetViewpoint } from "features/viewpoints/useSetViewpoint";
import { useAbortController } from "hooks/useAbortController";
import { useNotifyAppliedViewpoint } from "hooks/useNotifyAppliedViewpoint";
import { useResetAppliedViewpoint } from "hooks/useResetAppliedViewpoint";
import { selectUnitSystem, selectUxFlavor } from "slices/explorer/selectors";
import { AsyncStatus } from "types/misc";

export function WebviewerBridge({
    webviewer,
    resolve,
    reject,
}: {
    webviewer: Webviewer;
    resolve: () => void;
    reject: () => void;
}) {
    const hasResolved = useRef(false);
    const sceneStatus = useAppSelector(selectSceneStatus);
    useEffect(() => {
        const abortController = new AbortController();
        const run = async () => {
            if (sceneStatus.status === AsyncStatus.Success && !hasResolved.current) {
                try {
                    await webviewer.emitEvent("viewerReady", {
                        timestamp: Date.now(),
                        ...(webviewer.initOptions.sceneId !== undefined && { viewerId: webviewer.initOptions.sceneId }),
                    });
                    if (!abortController.signal.aborted) {
                        resolve();
                        hasResolved.current = true;
                    }
                } catch (error) {
                    if (!abortController.signal.aborted) {
                        console.error("Error emitting viewerReady event:", error);
                    }
                }
            }
            if (sceneStatus.status === AsyncStatus.Error && !hasResolved.current) {
                if (!abortController.signal.aborted) {
                    reject();
                    hasResolved.current = true;
                }
            }
        };
        run();
        return () => {
            abortController.abort();
        };
    }, [sceneStatus.status, resolve, reject, webviewer]);

    webviewer.uxFlavor = useSelector(selectUxFlavor);

    const unitSystem = useAppSelector(selectUnitSystem);
    const prevUnitSystemRef = useRef(unitSystem);

    useEffect(() => {
        if (prevUnitSystemRef.current !== unitSystem) {
            const displayUnits = convertUnitSystemToDisplayUnit(unitSystem);
            webviewer.eventEmitter.emit("projectSettingsUpdated", { displayUnits });
            prevUnitSystemRef.current = unitSystem;
        }
    }, [unitSystem, webviewer]);

    const highlighted = useHighlighted();
    webviewer.highlighted = highlighted;
    webviewer.highlightedLazy = useLazyHighlighted();
    webviewer.dispatchHighlighted = useDispatchHighlighted();
    webviewer.hiddenLazy = useLazyHidden();
    webviewer.dispatchHidden = useDispatchHidden();
    webviewer.selectionBasketLazy = useLazySelectionBasket();
    webviewer.dispatchSelectionBasket = useDispatchSelectionBasket();

    useEffect(() => {
        // Mark that the bridge is active to prevent duplicate event emissions from model API
        webviewer.bridgeActive = true;

        return () => {
            webviewer.bridgeActive = false;
        };
    }, [webviewer]);
    // Track previous selection and emit selectionChanged event
    // Note: Both lib.tsx methods (selectObjects, etc.) AND this useEffect emit events
    // to handle both API calls and user interactions. The eventEmitter provides best-effort
    // deduplication, though some duplicate emissions may occur in rapid selection changes.
    const previousSelectionRef = useRef<number[]>([]);
    const selectionQueueRef = useRef<Array<number[]>>([]);
    const processingRef = useRef(false);
    const processingAbortController = useRef<AbortController | null>(null);

    useResetAppliedViewpoint();
    useNotifyAppliedViewpoint();

    useEffect(() => {
        const currentSelection = [...highlighted.idArr];
        const previousSelection = previousSelectionRef.current;

        // Only queue if selection actually changed
        const currentSet = new Set(currentSelection);
        const previousSet = new Set(previousSelection);
        const hasSelectionChanged =
            currentSet.size !== previousSet.size || !currentSelection.every((id) => previousSet.has(id));

        if (!hasSelectionChanged) {
            return;
        }

        // Queue the new selection
        selectionQueueRef.current.push(currentSelection);

        const processQueue = async () => {
            // If already processing, the current loop will pick up the queued item
            // in its while loop, ensuring all queued selections are processed in order.
            if (processingRef.current) {
                return;
            }

            processingRef.current = true;
            processingAbortController.current = new AbortController();

            while (selectionQueueRef.current.length > 0) {
                if (processingAbortController.current.signal.aborted) {
                    break;
                }

                const nextSelection = selectionQueueRef.current.shift()!;
                const prevSelection = previousSelectionRef.current;

                if (!webviewer.model.hasSelectionListeners()) {
                    previousSelectionRef.current = nextSelection;
                    continue;
                }

                // Use Sets for O(1) lookup performance instead of O(n) with includes()
                const prevSet = new Set(prevSelection);
                const nextSet = new Set(nextSelection);
                const addedObjects = nextSelection.filter((id) => !prevSet.has(id));
                const removedObjects = prevSelection.filter((id) => !nextSet.has(id));

                await webviewer.model.emitSelectionEvents(
                    nextSelection,
                    prevSelection,
                    addedObjects,
                    removedObjects,
                    processingAbortController.current.signal,
                );
                previousSelectionRef.current = nextSelection;
            }
            processingRef.current = false;
            processingAbortController.current = null;
        };

        void processQueue();

        return () => {
            processingAbortController.current?.abort();
            selectionQueueRef.current = [];
        };
    }, [highlighted, webviewer]);

    const explorerGlobals = useExplorerGlobals();
    webviewer.explorerGlobals = explorerGlobals;

    // Bridge internal events to webviewer's public EventEmitter
    useEffect(() => {
        const internalEventEmitter = explorerGlobals.state.internalEventEmitter;

        if (!internalEventEmitter) {
            return;
        }

        // Subscribe to internal viewReset events and re-emit through webviewer's public API
        const unsubscribeViewReset = internalEventEmitter.on("viewReset", async (payload) => {
            await webviewer.eventEmitter.emit("viewReset", payload);
        });

        // Subscribe to internal intersectPointClick events and re-emit through webviewer's public API
        const unsubscribeIntersectPoint = internalEventEmitter.on("intersectPointClick", async (payload) => {
            await webviewer.eventEmitter.emit("intersectPointClick", payload);
        });

        const unsubscribeCameraUpdated = internalEventEmitter.on("cameraUpdated", async (payload) => {
            await webviewer.eventEmitter.emit("cameraUpdated", payload);
        });

        const unsubscribeAppResize = internalEventEmitter.on("appResize", async (payload) => {
            await webviewer.eventEmitter.emit("appResize", payload);
        });

        const unsubscribeSceneStatus = internalEventEmitter.on("sceneStatus", async (payload) => {
            await webviewer.eventEmitter.emit("sceneStatus", payload);
        });

        const unsubscribeViewpointChanged = internalEventEmitter.on("viewpointChanged", async (payload) => {
            await webviewer.eventEmitter.emit("viewpointChanged", payload);
        });

        return () => {
            unsubscribeViewReset();
            unsubscribeIntersectPoint();
            unsubscribeCameraUpdated();
            unsubscribeAppResize();
            unsubscribeSceneStatus();
            unsubscribeViewpointChanged();
        };
    }, [explorerGlobals.state.internalEventEmitter, webviewer]);

    const dispatchGlobals = explorerGlobals.dispatch;
    useEffect(() => {
        dispatchGlobals(explorerGlobalsActions.update({ webviewer }));
    }, [dispatchGlobals, webviewer]);

    webviewer.dispatchProcoreStampMenuItems = useDispatchProcoreStampMenuItems();
    webviewer.cameraState = useCameraState();
    webviewer.resetView = useResetView();
    webviewer.dispatchWindows = useDispatchWindows();
    webviewer.dispatchSidePanels = useDispatchSidePanels();
    webviewer.dispatchDrawers = useDispatchDrawers();
    webviewer.dispatchPropertiesTabs = useDispatchPropertiesTabs();
    webviewer.dispatchToolbars = useDispatchToolbars();
    webviewer.getToolbarButtonOrder = useToolbarButtonOrder();
    webviewer.svgRendererRef = useSvgRenderer();
    webviewer.dispatchCoachmarks = useDispatchCoachmarks();

    const panelMode = usePanelMode();
    webviewer.panelMode = panelMode;

    const sidePanelsState = useSidePanels();
    const drawersState = useDrawers();
    const dispatchSidePanels = webviewer.dispatchSidePanels;
    const dispatchDrawers = webviewer.dispatchDrawers;
    const prevPanelModeRef = useRef(panelMode);

    // Refs so the effect can read current state without re-running on every panel open/close
    const sidePanelsStateRef = useRef(sidePanelsState);
    sidePanelsStateRef.current = sidePanelsState;
    const drawersStateRef = useRef(drawersState);
    drawersStateRef.current = drawersState;

    useEffect(() => {
        if (prevPanelModeRef.current === panelMode) {
            return;
        }

        const oldMode = prevPanelModeRef.current;
        prevPanelModeRef.current = panelMode;

        if (!isMobileUxFlavor(webviewer.uxFlavor) || !webviewer.sdkWindowIds.size) {
            return;
        }

        if (oldMode === "sidePanel" && panelMode === "drawer") {
            // Transfer the most-recently-opened SDK panel (last in openPanels order).
            // Only one drawer slot exists, so we pick a single candidate rather than
            // looping over all sdkWindowIds (which would open each in turn, leaving
            // only the last-registered one visible).
            const openPanels = sidePanelsStateRef.current.openPanels;
            const candidateId = [...openPanels].reverse().find((id) => webviewer.sdkWindowIds.has(id));
            if (candidateId) {
                dispatchSidePanels(sidePanelsActions.closePanel(candidateId));
                dispatchDrawers(drawersActions.openDrawer(candidateId));
            }
        } else if (oldMode === "drawer" && panelMode === "sidePanel") {
            const openDrawer = drawersStateRef.current.openDrawer;
            if (openDrawer && webviewer.sdkWindowIds.has(openDrawer)) {
                dispatchDrawers(drawersActions.closeDrawer());
                dispatchSidePanels(sidePanelsActions.openPanel(openDrawer));
            }
        }
    }, [panelMode, webviewer, dispatchSidePanels, dispatchDrawers]);
    webviewer.getViewpointHook = useGetViewpoint();
    webviewer.setViewpointHook = useSetViewpoint();

    const [abortController] = useAbortController();
    webviewer.abortController = abortController;
    webviewer.object2normalColor = useAppSelector(selectObject2NormalColorOverrides);
    webviewer.object2xrayColor = useAppSelector(selectObject2XrayColorOverrides);
    webviewer.object2selectedColor = useAppSelector(selectObject2SelectedColorOverrides);
    webviewer.appDispatch = useAppDispatch();
    return null;
}
